// ============================================
// 💬 AgenteSuporteChat.jsx
// ============================================
// Chat flutuante de suporte que aparece em todas
// as telas do CRM. O usuário clica no botão no
// canto inferior direito e conversa com a IA
// que conhece todo o sistema.
//
// COMO FUNCIONA:
// - Botão flutuante no canto inferior direito
// - Abre uma janela de chat estilo WhatsApp
// - As perguntas vão para /api/chat-suporte.js
// - A IA responde com base na documentação do CRM
//
// PARA ADICIONAR AO APP.JS:
// import AgenteSuporteChat from './components/Suporte/AgenteSuporteChat';
// E no final do JSX, antes do </div> principal:
// <AgenteSuporteChat />
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2, ChevronDown } from 'lucide-react';
import useAppStore from '../../store/useAppStore';
import { cabecalhosComLogin } from '../../services/supabaseClient';

// ============================================
// 💬 MENSAGEM INICIAL DO AGENTE
// ============================================
const MENSAGEM_INICIAL = {
  role: 'assistant',
  content: 'Olá! 👋 Sou o assistente do **IntegrAI CRM**. Posso te ajudar com qualquer dúvida sobre o sistema — como lançar despesas, registrar vendas, configurar disparos, ou qualquer outra funcionalidade.\n\nComo posso te ajudar?',
  id: 'inicial'
};

// ============================================
// 🔧 HELPERS
// ============================================

// Renderiza markdown simples (negrito, itálico, listas, quebras de linha)
const renderizarMensagem = (texto) => {
  if (!texto) return null;

  const linhas = texto.split('\n');

  return linhas.map((linha, i) => {
    // Lista numerada
    if (/^\d+\.\s/.test(linha)) {
      const conteudo = linha.replace(/^\d+\.\s/, '');
      return (
        <div key={i} className="flex gap-2 my-0.5">
          <span className="text-indigo-400 font-bold flex-shrink-0">
            {linha.match(/^\d+/)[0]}.
          </span>
          <span dangerouslySetInnerHTML={{ __html: formatarInline(conteudo) }} />
        </div>
      );
    }
    // Lista com traço ou bullet
    if (/^[-•]\s/.test(linha)) {
      const conteudo = linha.replace(/^[-•]\s/, '');
      return (
        <div key={i} className="flex gap-2 my-0.5">
          <span className="text-indigo-400 flex-shrink-0">•</span>
          <span dangerouslySetInnerHTML={{ __html: formatarInline(conteudo) }} />
        </div>
      );
    }
    // Linha em branco
    if (linha.trim() === '') {
      return <div key={i} className="h-2" />;
    }
    // Linha normal
    return (
      <div key={i} dangerouslySetInnerHTML={{ __html: formatarInline(linha) }} />
    );
  });
};

const formatarInline = (texto) => {
  return texto
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-slate-700 px-1 rounded text-xs">$1</code>');
};

// ============================================
// 🎨 COMPONENTE PRINCIPAL
// ============================================
const AgenteSuporteChat = () => {
  const { tenant } = useAppStore();
  const [aberto, setAberto] = useState(false);
  const [minimizado, setMinimizado] = useState(false);
  const [mensagens, setMensagens] = useState([MENSAGEM_INICIAL]);
  const [input, setInput] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [pulsando, setPulsando] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll automático para a última mensagem
  useEffect(() => {
    if (aberto && !minimizado) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mensagens, aberto, minimizado]);

  // Foca o input ao abrir
  useEffect(() => {
    if (aberto && !minimizado) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [aberto, minimizado]);

  // Pulsa o botão após 8s para chamar atenção (só na primeira vez)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!aberto) setPulsando(true);
      setTimeout(() => setPulsando(false), 3000);
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // 📤 ENVIAR MENSAGEM
  // ============================================
  const enviarMensagem = async () => {
    const texto = input.trim();
    if (!texto || carregando) return;

    const novaMensagemUsuario = {
      role: 'user',
      content: texto,
      id: Date.now()
    };

    setMensagens(prev => [...prev, novaMensagemUsuario]);
    setInput('');
    setCarregando(true);
    setErro(null);

    try {
      // Monta histórico para a API (sem os IDs locais)
      const historico = [...mensagens, novaMensagemUsuario]
        .filter(m => m.id !== 'inicial') // Remove mensagem inicial local
        .map(({ role, content }) => ({ role, content }));

      const resposta = await fetch('/api/chat-suporte', {
        method: 'POST',
        headers: await cabecalhosComLogin(),
        body: JSON.stringify({ mensagens: historico, tenant_subdominio: tenant?.subdominio ?? null })
      });

      const dados = await resposta.json();

      if (!resposta.ok) {
        throw new Error(dados.erro || 'Erro ao obter resposta');
      }

      setMensagens(prev => [...prev, {
        role: 'assistant',
        content: dados.resposta,
        id: Date.now()
      }]);

    } catch (err) {
      console.error('Erro no chat de suporte:', err);
      setErro('Não consegui responder agora. Tente novamente em instantes.');
    } finally {
      setCarregando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  const limparConversa = () => {
    setMensagens([MENSAGEM_INICIAL]);
    setErro(null);
  };

  // ============================================
  // 🎨 RENDER
  // ============================================
  return (
    <>
      {/* ─── JANELA DO CHAT ─── */}
      {aberto && (
        <div
          className={`
            fixed bottom-24 right-5 z-50
            w-[370px] rounded-2xl shadow-2xl
            bg-slate-900 border border-slate-700
            flex flex-col overflow-hidden
            transition-all duration-300
            ${minimizado ? 'h-14' : 'h-[520px]'}
          `}
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {/* ─── HEADER ─── */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-indigo-600" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">Suporte IntegrAI</p>
                <p className="text-indigo-200 text-xs">Assistente do sistema</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMinimizado(!minimizado)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title={minimizado ? 'Expandir' : 'Minimizar'}
              >
                {minimizado ? <ChevronDown className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setAberto(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ─── MENSAGENS ─── */}
          {!minimizado && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-smooth">
                {mensagens.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    {/* Avatar */}
                    <div className={`
                      w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5
                      ${msg.role === 'user'
                        ? 'bg-indigo-500'
                        : 'bg-gradient-to-br from-violet-500 to-indigo-600'
                      }
                    `}>
                      {msg.role === 'user'
                        ? <User className="w-3.5 h-3.5 text-white" />
                        : <Bot className="w-3.5 h-3.5 text-white" />
                      }
                    </div>

                    {/* Balão */}
                    <div className={`
                      max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed
                      ${msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-tr-sm'
                        : 'bg-slate-800 text-slate-100 rounded-tl-sm border border-slate-700'
                      }
                    `}>
                      {renderizarMensagem(msg.content)}
                    </div>
                  </div>
                ))}

                {/* Loader enquanto aguarda resposta */}
                {carregando && (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-slate-800 border border-slate-700 px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                {/* Mensagem de erro */}
                {erro && (
                  <div className="text-center">
                    <p className="text-xs text-red-400 bg-red-900/30 border border-red-800/50 rounded-lg px-3 py-2">
                      {erro}
                    </p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* ─── FOOTER / INPUT ─── */}
              <div className="flex-shrink-0 p-3 border-t border-slate-700 bg-slate-900">
                {/* Botão limpar conversa */}
                {mensagens.length > 2 && (
                  <button
                    onClick={limparConversa}
                    className="text-xs text-slate-500 hover:text-slate-300 mb-2 transition-colors"
                  >
                    Limpar conversa
                  </button>
                )}

                <div className="flex gap-2 items-end">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Tire sua dúvida sobre o sistema..."
                    rows={1}
                    disabled={carregando}
                    className="
                      flex-1 bg-slate-800 border border-slate-600 rounded-xl
                      px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500
                      focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500
                      resize-none disabled:opacity-50 transition-colors
                    "
                    style={{ maxHeight: '100px' }}
                  />
                  <button
                    onClick={enviarMensagem}
                    disabled={!input.trim() || carregando}
                    className="
                      w-10 h-10 bg-indigo-600 hover:bg-indigo-500 rounded-xl
                      flex items-center justify-center flex-shrink-0
                      disabled:opacity-40 disabled:cursor-not-allowed
                      transition-all duration-150 active:scale-95
                    "
                  >
                    {carregando
                      ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                      : <Send className="w-4 h-4 text-white" />
                    }
                  </button>
                </div>
                <p className="text-xs text-slate-600 text-center mt-2">
                  Enter para enviar · Shift+Enter para nova linha
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── BOTÃO FLUTUANTE ─── */}
      <button
        onClick={() => {
          setAberto(!aberto);
          setMinimizado(false);
          setPulsando(false);
        }}
        className={`
          fixed bottom-5 right-5 z-50
          w-14 h-14 rounded-full shadow-lg
          bg-gradient-to-br from-indigo-600 to-violet-600
          flex items-center justify-center
          transition-all duration-300
          hover:scale-110 active:scale-95
          ${pulsando ? 'animate-bounce' : ''}
          ${aberto ? 'rotate-0' : ''}
        `}
        title="Suporte do sistema"
      >
        {aberto
          ? <X className="w-6 h-6 text-white" />
          : <MessageCircle className="w-6 h-6 text-white" />
        }

        {/* Badge de "disponível" */}
        {!aberto && (
          <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white" />
        )}
      </button>
    </>
  );
};

export default AgenteSuporteChat;
